import React, { useCallback } from 'react';
import { ScreenName } from '@helpcenter/constants';
import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import { ErrorMessage } from '@helpcenter/error-message';
import { Loading } from '@helpcenter/loading';
import type { WrappedComponentProps } from 'react-intl-next';
import { injectIntl } from 'react-intl-next';
import { LazySuspense } from 'react-loosely-lazy';

import { graphql, usePreloadedQuery } from 'react-relay';
import { PaperContent } from 'view/layout/paper';
import type { FetchError } from '@atlassian/help-center-common-util/fetch';
import { isFetchNetworkOrClientError } from '@atlassian/help-center-common-util/fetch';
import { useUFOComponentExperience } from '@atlassian/ufo';
import type { topicQuery } from './__generated__/topicQuery.graphql';
import HelpCenterTopic from './help-center-topic';
import { topicViewExperience } from './help-center-topic/experiences';
import messages from './messages';
import type { Props } from './types';

const Topic: React.FC<WrappedComponentProps & Props> = ({ intl, queryReference, topicId }): JSX.Element => {
    const data = usePreloadedQuery<topicQuery>(
        graphql`
            query topicQuery($cloudId: ID!, $helpCenterAri: ID!, $topicId: ID!) {
                ...helpCenterTopic_HelpCenterQuery
            }
        `,
        queryReference
    );
    useUFOComponentExperience(topicViewExperience);
    const markExperienceFailure = useCallback((error: FetchError) => {
        if (!isFetchNetworkOrClientError(error)) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            topicViewExperience.failure({
                metadata: {
                    error: error.message,
                },
            });
        }
    }, []);
    const renderError = useCallback(() => {
        return (
            <ErrorMessage
                title={intl.formatMessage(messages.genericHeading)}
                message={intl.formatMessage(messages.genericMessage)}
            />
        );
    }, [intl]);
    return (
        <PaperContent>
            <ErrorBoundary
                id={ScreenName.VIEW_TOPIC}
                packageName="topics"
                onError={markExperienceFailure}
                renderError={renderError}
            >
                <LazySuspense fallback={<Loading />}>
                    <HelpCenterTopic helpCenter={data} id={topicId} />
                </LazySuspense>
            </ErrorBoundary>
        </PaperContent>
    );
};

export default injectIntl(Topic);
